import React from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { cn } from './cn'

// Tailwind + Radix replacement for CoreUI's <CModal>/<CModalHeader>/etc.
// Radix Dialog supplies the accessibility behavior CoreUI's modal handled
// for us (focus trap, Escape to close, aria attributes) — we only style it.

const SIZE_CLASSES = {
  sm: 'tw-max-w-sm',
  md: 'tw-max-w-lg',
  lg: 'tw-max-w-3xl',
  xl: 'tw-max-w-5xl',
}

export const Dialog = RadixDialog.Root

export const DialogContent = React.forwardRef(
  ({ size = 'md', className, children, ...props }, ref) => (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-40" />
      <RadixDialog.Content
        ref={ref}
        className={cn(
          // Tailwind v2 (postcss7-compat, see migration.md) needs the base
          // `transform` utility present for the translate-* CSS variables
          // to actually apply — omitting it silently no-ops the centering.
          // Also note the prefix lands *before* the negative sign
          // (`tw--translate-x-1/2`, not `-tw-translate-x-1/2` like v3) —
          // get this backwards and the class just silently matches nothing.
          // Same story for `bg-black/50`-style slash opacity and `[...]`
          // arbitrary values above/below: both are v3-only syntax, so v2
          // gets bg-opacity-* and a maxHeight theme token (tailwind.config.js)
          // instead.
          'tw-fixed tw-left-1/2 tw-top-1/2 tw-z-50 tw-w-full',
          'tw-transform tw--translate-x-1/2 tw--translate-y-1/2',
          'tw-rounded tw-bg-white tw-shadow-lg tw-max-h-85vh tw-overflow-y-auto',
          SIZE_CLASSES[size] || SIZE_CLASSES.md,
          className
        )}
        {...props}
      >
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  )
)
DialogContent.displayName = 'DialogContent'

export const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn(
      'tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-4 tw-py-3',
      className
    )}
    {...props}
  />
)

export const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn('tw-text-lg tw-font-semibold tw-text-gray-900', className)}
    {...props}
  />
))
DialogTitle.displayName = 'DialogTitle'

export const DialogBody = ({ className, ...props }) => (
  <div className={cn('tw-p-4', className)} {...props} />
)

export const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-gray-200 tw-px-4 tw-py-3',
      className
    )}
    {...props}
  />
)
